import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Label,
} from "@exhousing/design-system";
import { ShieldCheck, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import {
  useAddContractGuarantor,
  useRemoveContractGuarantor,
} from "@/hooks/api/useContracts";
import { useGuarantors } from "@/hooks/api/useParties";
import type { components } from "@/types/api";

type Contract = components["schemas"]["ContractResponse"];

const SELECT = "flex h-11 w-full rounded-md border border-input bg-card px-3 text-sm";

export function ContractGuarantorsCard({ contract }: { contract: Contract }) {
  const editable = contract.status === "draft";
  const guarantors = useGuarantors();
  const add = useAddContractGuarantor(contract.id);
  const remove = useRemoveContractGuarantor(contract.id);

  const [guarantorId, setGuarantorId] = useState("");
  const [guaranteesTenantId, setGuaranteesTenantId] = useState("");

  const attached = contract.guarantors;
  const attachedIds = new Set(attached.map((g) => g.guarantor_id));
  // Only org guarantors not already on the contract can be attached.
  const options = (guarantors.data ?? []).filter((g) => !attachedIds.has(g.id));

  const tenantName = (id: string | null | undefined) =>
    contract.tenants.find((t) => t.tenant_id === id)?.display_name ?? null;

  const onAttach = () => {
    if (!guarantorId) return;
    add.mutate(
      {
        guarantor_id: guarantorId,
        guarantees_tenant_id: guaranteesTenantId || null,
      },
      {
        onSuccess: () => {
          toast.success("Guarantor attached");
          setGuarantorId("");
          setGuaranteesTenantId("");
        },
        onError: (e) => toast.error(e.message),
      },
    );
  };

  const onDetach = (id: string) => {
    remove.mutate(id, {
      onSuccess: () => toast.success("Guarantor removed"),
      onError: (e) => toast.error(e.message),
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Guarantors</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm text-muted-foreground">
          A guarantor (borgsteller) is jointly &amp; severally liable for the lease.
        </p>

        {attached.length > 0 ? (
          <ul className="divide-y divide-border/60 text-sm">
            {attached.map((g) => {
              const backs = tenantName(g.guarantees_tenant_id);
              return (
                <li key={g.guarantor_id} className="flex items-center gap-3 py-2.5">
                  <ShieldCheck className="size-4 shrink-0 text-muted-foreground" />
                  <span className="font-medium">{g.display_name}</span>
                  {backs && (
                    <span className="text-xs text-muted-foreground">
                      guarantees {backs}
                    </span>
                  )}
                  {editable && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="ml-auto"
                      disabled={remove.isPending}
                      onClick={() => onDetach(g.guarantor_id)}
                    >
                      <X className="size-4" />
                      Remove
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">No guarantors on this contract.</p>
        )}

        {editable ? (
          <div className="flex flex-wrap items-end gap-3 border-t border-border/60 pt-4">
            <div className="space-y-1.5">
              <Label htmlFor="guarantor_id">Guarantor</Label>
              <select
                id="guarantor_id"
                value={guarantorId}
                onChange={(e) => setGuarantorId(e.target.value)}
                className={`${SELECT} w-56`}
              >
                <option value="">Select a guarantor…</option>
                {options.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.contact.display_name}
                  </option>
                ))}
              </select>
            </div>
            {contract.tenants.length > 0 && (
              <div className="space-y-1.5">
                <Label htmlFor="guarantees_tenant_id">Backs tenant (optional)</Label>
                <select
                  id="guarantees_tenant_id"
                  value={guaranteesTenantId}
                  onChange={(e) => setGuaranteesTenantId(e.target.value)}
                  className={`${SELECT} w-56`}
                >
                  <option value="">All tenants</option>
                  {contract.tenants.map((t) => (
                    <option key={t.tenant_id} value={t.tenant_id}>
                      {t.display_name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <Button
              type="button"
              size="sm"
              disabled={!guarantorId || add.isPending}
              onClick={onAttach}
            >
              {add.isPending ? "Adding…" : "Attach guarantor"}
            </Button>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            Guarantors are part of the signed terms and locked once the contract is
            active.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
